import { SatelliteConversationFeatureType } from '@zg-rentals/ts-satellite-client';
import { useMemo } from 'react';
import useStreetEasyLeadManagementEnabled from './useStreetEasyLeadManagementEnabled';

type DisabledFeature = {
  displayMessage: string;
  feature: SatelliteConversationFeatureType;
};

/**
 * Structural over the three identical per-endpoint disabled-feature DTOs (lead-management
 * row, getMessages conversation, inbox conversation) so one accessor serves all surfaces.
 */
export type LeadRestrictionsSource = {
  disabledFeatures: Array<DisabledFeature>;
};

export type LeadRestrictions = {
  canInviteToApply: boolean;
  canRequestRenterProfile: boolean;
  canScheduleTour: boolean;
  canSendMessage: boolean;
  canViewMessages: boolean;
  canViewRenterJourney: boolean;
  canViewScreeningCriteria: boolean;
  canViewTourDetails: boolean;
  getDisabledMessage: (feature: SatelliteConversationFeatureType) => string | undefined;
};

/**
 * Normalizes a row/conversation's `disabledFeatures` into UI capabilities. Consumers gate on
 * these instead of reading `disabledFeatures` directly, and render `getDisabledMessage(...)`
 * copy verbatim. A feature value this build doesn't recognize matches no capability, so new
 * server-side features fail open until the client consumes a regenerated enum.
 *
 * Flag-unaware escape hatch for per-row map call sites (LeadManagementTable, LeadManagementCards),
 * which must compose the rollout flag themselves: `getLeadRestrictions(flagOn ? lead : null)`.
 * Component call sites should use the hook below instead, which folds the flag in.
 */
export const getLeadRestrictions = (lead: LeadRestrictionsSource | null | undefined): LeadRestrictions => {
  const disabledMessageByFeature = new Map<SatelliteConversationFeatureType, string>();
  for (const { displayMessage, feature } of lead?.disabledFeatures ?? []) {
    if (!disabledMessageByFeature.has(feature)) {
      disabledMessageByFeature.set(feature, displayMessage);
    }
  }
  const canUse = (feature: SatelliteConversationFeatureType): boolean => !disabledMessageByFeature.has(feature);
  return {
    canInviteToApply: canUse(SatelliteConversationFeatureType.INVITE_TO_APPLY),
    canRequestRenterProfile: canUse(SatelliteConversationFeatureType.REQUEST_RENTER_PROFILE),
    canScheduleTour: canUse(SatelliteConversationFeatureType.SCHEDULE_TOUR),
    canSendMessage: canUse(SatelliteConversationFeatureType.SEND_MESSAGE),
    canViewMessages: canUse(SatelliteConversationFeatureType.VIEW_MESSAGES),
    canViewRenterJourney: canUse(SatelliteConversationFeatureType.VIEW_RENTER_JOURNEY),
    canViewScreeningCriteria: canUse(SatelliteConversationFeatureType.VIEW_SCREENING_CRITERIA),
    canViewTourDetails: canUse(SatelliteConversationFeatureType.VIEW_TOUR_DETAILS),
    getDisabledMessage: (feature) => disabledMessageByFeature.get(feature),
  };
};

// The rollout flag is folded in here so a call site can't forget it and ship restrictions
// while the flag is off — flag-off passes null, which reads as unrestricted.
const useLeadRestrictions = (
  lead: LeadRestrictionsSource | null | undefined,
): LeadRestrictions & { isRestrictionRolloutEnabled: boolean } => {
  const isStreetEasyLeadManagementEnabled = useStreetEasyLeadManagementEnabled();
  return useMemo(
    () => ({
      ...getLeadRestrictions(isStreetEasyLeadManagementEnabled ? lead : null),
      // Exposed only so a call site can scope an additional restriction-adjacent condition
      // (e.g. treating unloaded data as restricted) to the same rollout. Never use it to
      // re-gate the capabilities above — those already account for it.
      isRestrictionRolloutEnabled: isStreetEasyLeadManagementEnabled,
    }),
    [isStreetEasyLeadManagementEnabled, lead],
  );
};

export default useLeadRestrictions;
